/// <reference types="@types/google.maps" />

import { useEffect, useRef, useState } from 'react';
import { config } from 'src/core/config';
import { loadScript } from 'src/utils/html';

export function promisifyGetDetails(
  placeId: string,
): Promise<google.maps.places.PlaceResult | null> {
  return new Promise((resolve) => {
    if (!placeId) {
      resolve(null);
    }

    const map = new google.maps.Map(document.createElement('div'));
    const placesService = new google.maps.places.PlacesService(map);

    placesService.getDetails(
      {
        placeId,
        fields: ['address_components'],
      },
      (place) => {
        resolve(place);
      },
    );
  });
}

async function getPlacePredictions(
  input: string,
): Promise<google.maps.places.AutocompletePrediction[]> {
  if (!input) {
    return [];
  }

  const autocompleteService = new google.maps.places.AutocompleteService();

  const { predictions } = await autocompleteService.getPlacePredictions({
    input,
    componentRestrictions: { country: ['us', 'ca'] },
  });

  return predictions ?? [];
}

export enum GoogleMapsAddressComponentTypes {
  SublocalityLevel1 = 'sublocality_level_1',
  Locality = 'locality',
  AdministrativeAreaLevel1 = 'administrative_area_level_1',
  PostalCode = 'postal_code',
}

export const useGoogleMapsSuggestions = ({ input }: { input: string }) => {
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  if (!loaded.current && !document.querySelector('#google-maps')) {
    loadScript(
      `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places&loading=async`,
      document.head,
      'google-maps',
    );

    loaded.current = true;
  }

  useEffect(() => {
    getPlacePredictions(input)
      .then((predictions) => {
        setSuggestions(predictions);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        console.error(e);
      });
  }, [input]);

  return {
    loading,
    suggestions,
  };
};
